<template>
  <div
    v-if="editor"
    class="h-full"
    :class="[
      {'max-w-1100 mx-auto w-full rounded-t-9 min-h-full' : settings && settings.mode === 'Paper shape'},
      {'bg-canvas': isProjectInstructionsAccepted && settings && settings.mode === 'Paper shape'}
    ]"
  >
    <div
      id="editor-wrapper"
      class="editor pt-40 md:pt-75 px-20 md:px-25 pb-10 mx-auto min-h-full rounded-9 flex-grow flex flex-col"
      :class="[maxWidth, { 'bg-canvas': isProjectInstructionsAccepted }]"
      @mousemove="setFocusMode(false)"
      @blur="setFocusMode(false)"
      @click="closeBlockColorPicker"
    >
      <MenuFloating
        v-show="allowEdit"
        ref="menuFloating"
        :editor="editor"
      />

      <MenuBubble
        v-show="allowEdit"
        :editor="editor"
        :is-table-active="!!isTableActive"
        :project="project"
      />

      <editor-content
        id="editor"
        autocomplete="new password"
        autocorrect="off"
        autocapitalize="on"
        :spellcheck="isGrammarCheck"
        class="editor__content h-full font-main text-canvas-color text-main flex flex-col flex-grow"
        :class="[{'pr-15': project.comments.length}, settings.fontFamily === 'Silka mono' ? 'silka-font': 'tracking-normal']"
        :editor="editor"
      />

      <TableControls
        v-if="!!isTableActive"
        :key="isTableActive"
        :style="{ top: `${ tableTop }px` }"
        :editor="editor"
        :active-table="activeTable"
      />

      <ProjectInstruction />
      <TemplateModal :editor="editor" />

      <!-- mention -->
      <div v-show="showMentions" ref="mentionRef" :class="isDarkMode ? 'bg-dark-800' : 'bg-white'">
        <template v-if="hasMentions">
          <div
            v-for="(item, index) in filteredMentions"
            :key="item.id"
            :ref="el => mentionRefs[index] = el"
            class="flex items-center py-5 px-10 cursor-pointer last:mb-0 no-scroll-bar last:rounded-b-10 text-14"
            :class="[
              isDarkMode ? 'text-grey-500' : 'text-dark-600',
              {'bg-selection font-semibold': navigatedMentionIndex === index},
              {'mention-project': item.label === 'pages'},
              {'mention-user': item.label === 'users'}
            ]"
            @mouseenter="onHover"
            @mouseleave="outHover(index, $event)"
            @click.stop="selectMention(item)"
          >
            <div v-if="item.label === 'users'">
              <MentionUser :user="item" />
            </div>
            <div v-else class="flex items-center">
              <img v-if="item.icon" class="w-20 h-20 mr-10" :src="require(`@/assets/images/icons/${item.icon}.png`)" alt="icon">
              <img v-else class="w-20 h-20 mr-10" src="@/assets/images/icons/purple_circle.png" alt="icon">
              <span> {{ item.title }} </span>
            </div>
          </div>
        </template>
      </div>
      <!-- emoji -->
      <div v-show="showEmoji" ref="emojiRef" :class="isDarkMode ? 'bg-dark-800' : 'bg-white'">
        <template v-if="hasResults">
          <div
            v-for="(item, index) in filteredItems"
            :key="item.id"
            :ref="el => emojiRefs[index] = el"
            class="px-20 py-10 cursor-pointer last:mb-0 no-scroll-bar first:rounded-t-4 last:rounded-b-4"
            :class="[isDarkMode ? 'text-grey-500' : 'text-grey-main', {'bg-selection': navigatedEmojiIndex === index}]"
            @mouseenter="onHover"
            @mouseleave="outHover(index, $event)"
            @click.stop="selectEmoji(item, editor)"
          >
            {{ item.char }} <span class="ml-5">:{{ item.name }}:</span>
          </div>
        </template>
      </div>

      <portal-target name="editor" multiple />
    </div>
  </div>
</template>

<script>
import { Editor, EditorContent } from '@tiptap/vue-3'
import { defaultExtensions } from '@/tiptap/extensions'

// import CollaborationCursor from '@tiptap/extension-collaboration-cursor'
// import Collaboration from '@tiptap/extension-collaboration'
// import * as Y from 'yjs'
// import { WebsocketProvider } from '@/tiptap/utils/WebsocketProvider'

import MenuBubble from '@/components/workspace/MenuBubble'
import MenuFloating from '@/components/workspace/MenuFloating'
import TableControls from '@/components/workspace/TableControls.vue'
import MentionUser from '@/components/workspace/MentionUser'

import TemplateModal from '@/components/templates/TemplateModal'

import ProjectInstruction from '@/components/instructions/ProjectInstruction'

import MentionMixin from '@/mixins/MentionMixin.js'
import EmojiMixin from '@/mixins/EmojiMixin.js'
import ImageUploadMixin from '@/mixins/ImageUploadMixin.js'

import { commentService, attachmentService, projectService } from '@/services'

import designConfig from '@/core/design-config'
import { generateHTML, selectText, countWords, unselectText } from '@/core/helper-functions'

import { defineComponent, computed, ref, watch, onBeforeUnmount, onMounted } from 'vue'
import { useStore } from 'vuex'
import { onBeforeRouteLeave, onBeforeRouteUpdate, useRoute } from 'vue-router'

export default defineComponent({
  components: {
    EditorContent,
    MenuBubble,
    MenuFloating,
    ProjectInstruction,
    TableControls,
    TemplateModal,
    MentionUser
  },

  props: {
    project: { type: Object },
    bookId: { type: Number },
    projects: { type: Array },
    allowEdit: { type: Boolean },
    isShared: { type: Boolean }
  },

  setup (props) {
    window.addEventListener('scroll', handleScroll)

    const { state, getters, dispatch, commit } = useStore()
    // const router = useRouter()
    const route = useRoute()

    // mixins
    const emoji = EmojiMixin()
    const mention = MentionMixin()
    const imageUpload = ImageUploadMixin(props)

    // data
    let scrollPosition = window.pageYOffset
    let scrollTimer = null
    let timer = null
    // image
    let existingImages = null
    // table
    const activeTable = ref(null)
    const tableTop = ref(0)
    // editor
    const editoKey = ref(1)
    // refs
    const menuFloating = ref(null)
    const suggestions = ref(null)
    // computed
    // const user = computed(() => state.profile.user)
    // const team = computed(() => state.team.team)
    // const token = computed(() => state.auth.token)
    const share = computed(() => state.project.share)
    const files = computed(() => state.file.files)
    const book = computed(() => state.book.book)
    const books = computed(() => state.book.books)
    const settings = computed(() => state.settings.settings)
    const isTableActive = computed(() => state.project.isTableActive)
    const isGrammarCheck = computed(() => state.settings.isGrammarCheck)
    const isMobile = computed(() => getters.isMobile)
    const hasTemplates = computed(() => getters.hasTemplates)
    const isDarkMode = computed(() => getters.isDarkMode)
    const isTeamAccount = computed(() => getters.isTeamAccount)
    const allowEdit = computed(() => props.allowEdit)
    const maxWidth = computed(() => `max-w-${designConfig.columns[settings.value?.columnWidth]}`)
    const isProjectInstructionsAccepted = computed(() => state.profile?.user?.profile.showProjectOnboarding)
    const isHeadquarters = computed(() => route.name === 'Headquarters')
    // const isSharePage = computed(() => route.name === 'Share')

    // editor
    // const ydoc = new Y.Doc()
    // const teamQuery = isTeamAccount.value ? `&teamId=${team.value?.id}` : ''
    // const query = token.value && !isSharePage.value ? `token=${token.value}${teamQuery}` : `share=${share.value.id}`

    // const provider = ref(null)

    // provider.value = new WebsocketProvider(
    //     `${process.env.VUE_APP_WEBSOCKET}/api/collaborations`,
    //     `${props.project.id}?${query}&projectId=${props.project.id}`,
    //     ydoc
    // )

    // provider.value.on('message', (e) => {
    //   if (e.code >= 4300) {
    //     editor.setOptions({
    //       editable: false
    //     })

    //     if (e.message) commit('setNotification', { isVisible: true, message: e.message, color: 'error' })

    //     if (e.code === 4401) {
    //       const url = window.location.search ? window.location.pathname + window.location.search : window.location.pathname
    //       router.push({ name: 'Unauthorized', params: { savedUrl: url } }).catch(() => {})
    //       provider.value.destroy()
    //     } else if (e.code === 4404) {
    //       provider.value.destroy()
    //       commit('setShare', null)
    //     }
    //   }
    // })

    const editor = new Editor({
      onUpdate: ({ transaction }) => {
        save(editor.getHTML(), editor.getJSON())
        commit('setWordsCount', countWords(transaction.doc))
        setFocusMode(true)
        commit('setCharactersCount', transaction.doc.textContent.length)
        tableTopPosition()
        restoreOrDeleteImage()

        const { content } = editor.getJSON()
        const allParagraphs = content.filter(item => item.type === 'paragraph')

        if (transaction.doc.textContent.trim() === '' && allParagraphs.length < 2) {
          if (content.every(item => item.type !== 'addTemplate') && hasTemplates.value) {
            editor.chain().addTemplateButton().run()
          }
        } else {
          editor.chain().removeTemplateButton().run()
        }
      },
      onCreate: () => {
        const { content } = editor.getJSON()
        const allParagraphs = content.filter(item => item.type === 'paragraph')

        if (editor.state.doc.textContent.trim() === '' && allParagraphs.length < 2) {
          if (content.every(item => item.type !== 'addTemplate') && hasTemplates.value) {
            editor.chain().addTemplateButton().run()
          }
        }
        setTimeout(() => {
          clearComments()
        }, 500)
      },
      extensions: [
        ...defaultExtensions({
          highlight: {
            multicolor: true
          },
          dropcursor: {
            color: designConfig.themes[settings.value?.theme]?.linkColor,
            width: 2
          },
          mention: {
            items: () => {
              if (isTeamAccount.value) {
                const currBook = books.value.find(b => b.id === book.value.id)
                let users = isTeamAccount.value ? currBook?.shareWith : null

                if (isHeadquarters.value) users = state.team.members

                const mappedProjects = props.projects.map((p) => ({ ...p, label: 'pages' }))
                const mappedUsers = users.map((u) => ({ ...u, label: 'users', title: u.firstName }))

                return [...mappedProjects, ...mappedUsers]
              } else {
                return props.projects
              }
            },
            onEnter: mention.onEnter,
            onKeyDown: mention.onKeyDown,
            onChange: mention.onChange,
            onExit: mention.onExit,
            onFilter: mention.onFilter
          },
          taskItem: {
            nested: true
          },
          emoji: {
            onEnter: emoji.onEnter,
            onKeyDown: emoji.onKeyDown,
            onChange: emoji.onChange,
            onExit: emoji.onExit,
            onFilter: emoji.onFilter
          },
          image: {
            uploadFunc: imageUpload.upload
          }
        })
        // Collaboration.configure({
        //   document: ydoc
        // })
        // CollaborationCursor.configure({
        //   provider: provider.value,
        //   user: {
        //     name: user.value?.profile.firstName || 'Guest',
        //     color: user.value?.profile.color || `#${('000000' + Math.floor(Math.random() * 16777215).toString(16)).slice(-6)}`
        //   }
        // })
      ],
      disablePasteRules: ['italic'],
      content: props.project?.document || props.project?.body || '',
      editable: allowEdit.value
    })

    // watch
    watch(allowEdit, (value) => {
      editor.setOptions({
        editable: value
      })
    })

    watch(isGrammarCheck, () => (editoKey.value = Math.random())) // update the editor after a spell check changing

    onMounted(() => {
      // get exitsting images ids
      existingImages = checkImages(editor.getJSON())
      // this.existingComments = this.checkComments(editor.getJSON())
      commit('setWordsCount', countWords(editor.state.tr.doc))
      commit('setCharactersCount', editor.state.tr.doc.textContent.length)
    })

    watch(isTableActive, (active) => {
      if (active) {
        tableTopPosition()
      }
    })

    onBeforeUnmount(async () => {
      editor.destroy()
      // provider.value.destroy()
      window.removeEventListener('scroll', handleScroll)
      commit('setTableActive', false)
    })

    onBeforeRouteLeave((to) => {
      if (to.name !== 'Login') clearAttachments()

      if (Object.values(files.value).some(item => item.percent >= 0 && item.bookId === book.value.id)) {
        commit('setNotification', {
          isVisible: true,
          message: 'Your file is still uploading, please wait for it to finish'
        })
        return false
      }
    })

    onBeforeRouteUpdate(() => {
      clearAttachments()

      if (Object.values(files.value).some(item => item.percent >= 0 && item.bookId === book.value.id)) {
        commit('setNotification', {
          isVisible: true,
          message: 'Your file is still uploading, please wait for it to finish'
        })
        return false
      }
    })

    function clearAttachments () {
      if (props.isShared) {
        attachmentService.clearShared(props.project.id, share.value.id, { ids: existingImages })
      } else {
        attachmentService.clear({
          projectId: props.project.id,
          data: { ids: existingImages }
        })
      }
    }

    function restoreOrDeleteImage () {
      const newImages = checkImages(editor.getJSON())

      if (existingImages?.length > newImages?.length) {
        const imageId = existingImages.filter(imageId => !newImages.includes(imageId))[0]

        commit('deleteFile', imageId)

        if (props.isShared) {
          if (imageId) {
            attachmentService.deleteShared(props.project.id, share.value.id, { id: imageId })
              .then(() => dispatch('getSizeOfSharedProject', props.project.id))
          }
        } else {
          if (imageId) {
            attachmentService.delete({
              projectId: props.project.id,
              data: { id: imageId }
            })
              .then(() => dispatch('getSize', props.project.id))
          }
        }
      } else if (newImages?.length > existingImages?.length) {
        const imageId = newImages.filter(imageId => !existingImages.includes(imageId))[0]

        if (props.isShared) {
          if (props.isShared.mode === 'write' && imageId) {
            attachmentService.restoreShared(props.project.id, share.value.id, { id: imageId })
              .then(() => dispatch('getSizeOfSharedProject', props.project.id))
          }
        } else {
          if (imageId) {
            attachmentService.restore({
              projectId: props.project.id,
              data: { id: imageId }
            })
              .then(() => dispatch('getSize', props.project.id))
          }
        }
      }

      existingImages = newImages
    }

    function checkImages (json) {
      let nodes = []

      json.content
        .filter(item => item.type === 'paragraph' && item.content?.length)
        .map(item => item.content)
        .forEach(item => nodes.push(...item))

      nodes = nodes.filter(item => item.type === 'image' || item.type === 'file')

      return nodes.map(item => +item.attrs.id)
    }

    function setFocusMode (value) {
      commit('setFocusMode', value)
    }

    function handleScroll (e) {
      clearTimeout(scrollTimer)
      const currentScrollPos = window.pageYOffset

      if (isMobile.value) {
        // stop scrolling
        scrollTimer = setTimeout(() => {
          commit('updateHeader', true)
        }, 400)

        if (scrollPosition > currentScrollPos) {
          commit('updateHeader', true)
        } else {
          commit('updateHeader', window.pageYOffset < 57)
        }
        scrollPosition = currentScrollPos
      } else {
        commit('updateHeader', true)
      }
    }

    function tableTopPosition () {
      if (isTableActive.value) {
        activeTable.value = document.querySelector('.tableWrapper.activeTable table')

        if (!activeTable.value) return

        if (isMobile.value) {
          tableTop.value = (activeTable.value.getBoundingClientRect().bottom - 50) + window.pageYOffset
        } else {
          const main = document.getElementById('main')
          tableTop.value = (activeTable.value.getBoundingClientRect().bottom - 75) + main.scrollTop
        }
      }
    }

    function closeBlockColorPicker () {
      setFocusMode(true)
      if (menuFloating.value) menuFloating.value.closeBlockColorPicker()
    }

    function deleteComment (id) {
      const comment = props.project.comments.find(item => item.id === id)

      if (comment) {
        commentService.delete(id).then(() => {
          commit('updateComments', props.project.comments.filter(item => item.id !== id))
          editor.chain().focus().removeCommentMark().run()
          commit('loading', false)
          unselectText()
        })
      } else {
        setTimeout(() => {
          editor.chain().focus().removeCommentMark().run()
          unselectText()
          commit('loading', false)
        }, 100)
      }
    }

    function clearComments () {
      const marks = [...document.querySelectorAll('[commentid]')]
      const nodes = [...document.querySelectorAll('[contentid]')]

      if (marks.length !== nodes.length) {
        // remove marks
        marks.forEach(item => {
          const id = item.getAttribute('commentid')
          const node = document.querySelector(`span[contentid="${id}"]`)
          const mark = document.querySelector(`span[commentid="${id}"]`)

          if (!node) {
            setTimeout(() => {
              selectText(mark)
              deleteComment(+id)
            }, 100)
          }
        })
        // remove nodes
        nodes.forEach(item => {
          const id = item.getAttribute('contentid')
          const mark = document.querySelector(`span[commentid="${id}"]`)

          if (!mark) {
            item.parentNode.remove()
            const hasComment = props.project.comments.find(item => item.id === id)

            if (hasComment) {
              commentService.delete(id).then(() => {
                commit('updateComments', props.project.comments.filter(item => item.id !== id))
              })
            }
          }
        })
      }
    }

    function editSharedProject (text, json) {
      projectService.editSharedProject(
        props.project.id,
        share.value.id,
        {
          title: props.project.title,
          body: text,
          document: json,
          html: generateHTML(text, settings.value, props.project.title)
        }
      ).then(() => {
        commit('setCurrentProject', {
          ...props.project,
          body: text,
          document: json,
          html: generateHTML(text, settings.value, props.project.title)
        })
      })
    }

    function save (text, json) {
      clearTimeout(timer)
      timer = setTimeout(() => {
        clearComments()

        if (props.isShared && share.value) {
          editSharedProject(text, json)
        } else {
          dispatch('editProject', {
            id: props.project.id,
            data: {
              title: props.project.title,
              body: text,
              document: json,
              html: generateHTML(text, settings.value, props.project.title)
            }
          }).then(() => {
            commit('setCurrentProject', {
              ...props.project,
              document: json,
              html: generateHTML(text, settings.value, props.project.title)
            })
          })
        }
      }, 400)
    }

    function onHover (e) {
      e.target.style = `background-color: ${designConfig.themes[settings.value.theme].selectionColor}`
    }

    function outHover (index, e) {
      if (emoji.navigatedEmojiIndex.value === index) {
        e.target.style = `background-color: ${designConfig.themes[settings.value.theme].selectionColor}`
      } else {
        e.target.style = 'background-color: transparent'
      }
    }

    return {
      // mention
      navigatedMentionIndex: mention.navigatedMentionIndex,
      filteredMentions: mention.filteredMentions,
      selectMention: mention.selectMention,
      showMentions: mention.showMentions,
      hasMentions: mention.hasMentions,
      mentionRefs: mention.mentionRefs,
      mentionRef: mention.mentionRef,
      // emoji
      navigatedEmojiIndex: emoji.navigatedEmojiIndex,
      filteredItems: emoji.filteredItems,
      selectEmoji: emoji.selectEmoji,
      hasResults: emoji.hasResults,
      showEmoji: emoji.showEmoji,
      emojiRefs: emoji.emojiRefs,
      emojiRef: emoji.emojiRef,
      // data
      editoKey,
      activeTable,
      tableTop,
      editor,
      // refs
      menuFloating,
      suggestions,
      // computed
      settings,
      isTableActive,
      isGrammarCheck,
      isDarkMode,
      maxWidth,
      isProjectInstructionsAccepted,

      // functions
      setFocusMode,
      closeBlockColorPicker,
      onHover,
      outHover
    }
  }
})
</script>
